/*
XOL-67 (v2.2, decision 172; UX spec C6): the Google Places behind the map atom's "Find a place" row. Pure functions that take the `google` object
(window.google, after gmap-vue has loaded it with the `places` library) so a spec can hand them a fake, like rtaNameLookup. Nothing here touches Vue,
the store or axios, and nothing here draws or moves a pin: a search only pans and zooms the map.

  MIN_CHARS, COUNTRY               at least 3 characters, Malaysia only
  placesAvailable(google)          is there a Google with the `places` library (AutocompleteService, Geocoder)?
  newSession(google)               a fresh AutocompleteSessionToken (one per search session), or null
  searchPlaces(google, query, session)   { status: 'ok' | 'empty' | 'short' | 'error', items: [{ value, label, main, secondary }] }; never rejects
  resolvePlace(google, placeId)    { location: { lat, lng }, viewport } from the Geocoder by placeId, or null; never rejects
  fitPlace(google, map, place)     fit the map to the place (a viewport capped at zoom 17), else centre on it at 17; places NO pin
  noDataText(state, query)         the select's dynamic no-data text
  afterPickLine(description, label, hasPin)   the line under the row after a pick
  the copy: LOADING_TEXT, PLACEHOLDER, PLACEHOLDER_OFF, MSG_OFF, MSG_NOT_FOUND, ARIA_LABEL

The Geocoder resolves a picked placeId (not Places Details). A session that ends in a Geocoder call is NOT billed as one session: Autocomplete requests are billed per
prediction request, so what keeps the calls down is the 3-character minimum and Element's 300 ms remote debounce (the session token is kept as good practice).

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

export var MIN_CHARS = 3
export var COUNTRY = 'my'

// The zoom a pick never goes closer than (street level, as the pin fit of the atom).
export var MAX_ZOOM = 17

export var LABEL = 'Find a place'
export var PLACEHOLDER = 'Search an address, township or place…'
export var PLACEHOLDER_OFF = 'Place search isn\'t available'
export var LOADING_TEXT = 'Searching…'
export var ARIA_LABEL = 'Find a place on the map'
export var MSG_OFF = 'Place search isn\'t available right now. Use the Locality, or type Lat / Lng.'
export var MSG_NOT_FOUND = 'Couldn\'t find that place on the map. Try another search.'

var OK = 'OK'
var ZERO = 'ZERO_RESULTS'

function text (v) {
  return v === undefined || v === null ? '' : String(v)
}

/** Is there a Google with the `places` library loaded? */
export function placesAvailable (google) {
  return !!google && !!google.maps && !!google.maps.places &&
    typeof google.maps.places.AutocompleteService === 'function' && typeof google.maps.Geocoder === 'function'
}

/** A new session token: the predictions of one search session and its pick are billed as one. Null when the library is not there. */
export function newSession (google) {
  try {
    return new google.maps.places.AutocompleteSessionToken()
  } catch (e) {
    return null
  }
}

/**
 * The predictions for `query`: Malaysia only, from MIN_CHARS characters (fewer is 'short' and asks Google nothing). 'empty' is Google's ZERO_RESULTS;
 * 'error' is any other status, no Google, or a throw. Each item: { value: placeId, label: description, main, secondary } (the two lines of a row).
 */
export function searchPlaces (google, query, session) {
  var q = text(query).trim()

  return new Promise(function (resolve) {
    if (q.length < MIN_CHARS) {
      resolve({ status: 'short', items: [] })
      return
    }

    try {
      var request = { input: q, componentRestrictions: { country: COUNTRY } }

      if (session) {
        request.sessionToken = session
      }

      new google.maps.places.AutocompleteService().getPlacePredictions(request, function (predictions, status) {
        if (status === ZERO) {
          resolve({ status: 'empty', items: [] })
          return
        }

        if (status !== OK || !Array.isArray(predictions)) {
          resolve({ status: 'error', items: [] })
          return
        }

        resolve({
          status: predictions.length ? 'ok' : 'empty',
          items: predictions.filter(function (p) { return !!p && !!p.place_id }).map(function (p) {
            var f = p.structured_formatting || {}

            return { value: p.place_id, label: text(p.description), main: text(f.main_text || p.description), secondary: text(f.secondary_text) }
          })
        })
      })
    } catch (e) {
      resolve({ status: 'error', items: [] })
    }
  })
}

/**
 * A picked prediction as a place the map can show: the Geocoder by placeId. { location: { lat, lng }, viewport } (`viewport` is Google's bounds, or
 * null when the place has none), or null when it did not resolve.
 */
export function resolvePlace (google, placeId) {
  return new Promise(function (resolve) {
    if (!text(placeId)) {
      resolve(null)
      return
    }

    try {
      new google.maps.Geocoder().geocode({ placeId: placeId }, function (results, status) {
        var geometry = status === OK && Array.isArray(results) && results[0] ? results[0].geometry : null
        var at = geometry ? geometry.location : null

        if (!at || typeof at.lat !== 'function' || typeof at.lng !== 'function') {
          resolve(null)
          return
        }

        resolve({ location: { lat: at.lat(), lng: at.lng() }, viewport: geometry.viewport || null })
      })
    } catch (e) {
      resolve(null)
    }
  })
}

/**
 * Show the place on `map` (a google.maps.Map): fit its viewport and, once the map is idle, pull the zoom back to MAX_ZOOM if the fit went closer;
 * with no viewport, centre on the point at MAX_ZOOM. It places NO pin and moves none: the caller only tells the user how to put the pin there.
 */
export function fitPlace (google, map, place) {
  if (!map || !place) {
    return
  }

  if (place.viewport) {
    map.fitBounds(place.viewport)

    google.maps.event.addListenerOnce(map, 'idle', function () {
      if (map.getZoom() > MAX_ZOOM) {
        map.setZoom(MAX_ZOOM)
      }
    })

    return
  }

  map.setCenter(place.location)
  map.setZoom(MAX_ZOOM)
}

/** The select's no-data text for a state of the search: the query is shown as typed, cut at 40 characters. */
export function noDataText (state, query) {
  var q = text(query).trim()

  if (state === 'error') {
    return 'Couldn\'t search just now. Try again.'
  }

  if (state === 'short' || q.length < MIN_CHARS) {
    return 'Keep typing — at least ' + MIN_CHARS + ' characters'
  }

  return 'No places in Malaysia match "' + (q.length > 40 ? q.slice(0, 40) + '…' : q) + '".'
}

/**
 * The line under the row after a pick (UX spec C6). `label` is the pin button's visible label (Add / Center), `hasPin` whether the request point is
 * already on the map. A pick never moves the pin, and the line says how to.
 */
export function afterPickLine (description, label, hasPin) {
  var where = 'The map is centered on ' + text(description) + '.'

  if (!label) {
    return where
  }

  return hasPin
    ? where + ' The requested point hasn\'t moved: press ' + label + ' to move it here.'
    : where + ' Press ' + label + ' to put the pin there.'
}
